/***
 *
 *   ACCOUNTS
 *   Manage and monitor all customer accounts
 *
 **********/

import { useState, useMemo, useContext, useEffect, useRef } from 'react';
import { parseLicenses, splitDateTime, convertToLocalTime } from '../utility/helper';
import { buildWebappUrl } from '../utility/webapp';
import { ViewContext, AuthContext, Animate, useAPI, useDebounce, LicenseBadge, StatusBadge, MetricBadge, PracticeAreaTags, DataTable } from 'components/lib';
import { useNavigate } from 'react-router-dom';
import Style from './accounts.tailwind.js';
import { LICENSE_TIERS, PROMPTS_PRESETS, FILTER_DEFAULTS, hasActiveFilter, COLS, PAGE_SIZES, STRINGS, licenseChipLabel, practiceAreasChipLabel, avgPromptsChipLabel, dateChipLabel } from './accounts.constants.js';

// ─── Chevron icon (SVG stroke color must be dynamic, inline style is intentional) ─

function ChevronDown({ color = '#9ca3af' }) {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"/>
    </svg>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────

function FilterChip({ label, active, onClick, children, dropdownOpen, dropdownRef }) {
  const dropdownId = `filter-chip-dropdown-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const buttonId   = `${dropdownId}-button`;
  return (
    <div className={Style.chipWrap} ref={dropdownRef}>
      <button
        id={buttonId}
        onClick={onClick}
        aria-expanded={dropdownOpen}
        aria-haspopup="dialog"
        aria-controls={dropdownId}
        className={`${Style.chipBase} ${active ? Style.chipActive : Style.chipInactive}`}
      >
        {label}
        <ChevronDown color={active ? '#2563eb' : '#9ca3af'} />
      </button>
      {dropdownOpen && (
        <div id={dropdownId} role="dialog" aria-labelledby={buttonId} className={Style.chipDropdown}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── License dropdown ─────────────────────────────────────────────────────────

function LicenseDropdown({ filters, setFilters }) {
  function toggleTier(tier) {
    setFilters(prev => {
      const has = prev.licenseTiers.includes(tier);
      return {
        ...prev,
        licenseTiers: has
          ? prev.licenseTiers.filter(t => t !== tier)
          : [...prev.licenseTiers, tier],
      };
    });
  }

  return (
    <div className={Style.dropdownPad}>
      <div className={Style.dropdownLabel}>{STRINGS.licenseDropdownLabel}</div>
      <div className={Style.dropdownChips}>
        {LICENSE_TIERS.map(tier => {
          const isActive = filters.licenseTiers.includes(tier);
          return (
            <button
              key={tier}
              onClick={() => toggleTier(tier)}
              className={`${Style.tierBtnBase} ${isActive ? Style.tierBtnActive : Style.tierBtnInactive}`}
            >
              {tier}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Practice areas dropdown ──────────────────────────────────────────────────

function PracticeAreasDropdown({ filters, setFilters, allPracticeAreas }) {
  const [search, setSearch] = useState('');

  function toggleArea(area) {
    setFilters(prev => {
      const has = prev.practiceAreasFilter.includes(area);
      return {
        ...prev,
        practiceAreasFilter: has
          ? prev.practiceAreasFilter.filter(a => a !== area)
          : [...prev.practiceAreasFilter, area],
      };
    });
  }

  const visible = allPracticeAreas.filter(a =>
    !search.trim() || a.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className={Style.dropdownPadWide}>
      <label htmlFor="practice-area-search" className={Style.dropdownLabel}>{STRINGS.practiceDropdownLabel}</label>
      <input
        id="practice-area-search"
        type="text"
        placeholder={STRINGS.practiceSearchPlaceholder}
        value={search}
        onChange={e => setSearch(e.target.value)}
        className={Style.practiceSearch}
      />
      <div className={Style.practiceList}>
        {visible.length === 0 && (
          <span className={Style.practiceEmpty}>{STRINGS.practiceEmpty}</span>
        )}
        {visible.map(area => {
          const checked = filters.practiceAreasFilter.includes(area);
          return (
            <label key={area} className={Style.practiceItem}>
              <input
                type="checkbox"
                checked={checked}
                onChange={() => toggleArea(area)}
                className={Style.practiceCheckbox}
              />
              {area}
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Avg prompts dropdown ─────────────────────────────────────────────────────

function AvgPromptsDropdown({ filters, setFilters }) {
  return (
    <div className={Style.dropdownPad}>
      <div className={Style.dropdownLabel}>{STRINGS.avgPromptsDropdownLabel}</div>
      <div className={Style.avgOptionsList}>
        {PROMPTS_PRESETS.map(preset => {
          const isActive = filters.avgPromptsPreset === preset.value;
          return (
            <button
              key={preset.value}
              onClick={() => setFilters(prev => ({ ...prev, avgPromptsPreset: preset.value }))}
              className={`${Style.avgBtnBase} ${isActive ? Style.avgBtnActive : Style.avgBtnInactive}`}
            >
              {preset.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Date created dropdown ────────────────────────────────────────────────────

function DateDropdown({ filters, setFilters }) {
  return (
    <div className={Style.dropdownPadWide}>
      <div className={Style.dropdownLabel}>{STRINGS.dateDropdownLabel}</div>
      <div className={Style.dateCol}>
        <div className={Style.dateRow}>
          <label htmlFor="date-from" className={Style.dateFromToLabel}>{STRINGS.dateFrom}</label>
          <input
            id="date-from"
            type="date"
            value={filters.dateFrom}
            onChange={e => setFilters(p => ({ ...p, dateFrom: e.target.value }))}
            className={Style.dateInput}
          />
        </div>
        <div className={Style.dateRow}>
          <label htmlFor="date-to" className={Style.dateFromToLabel}>{STRINGS.dateTo}</label>
          <input
            id="date-to"
            type="date"
            value={filters.dateTo}
            onChange={e => setFilters(p => ({ ...p, dateTo: e.target.value }))}
            className={Style.dateInput}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function openInNewTab(url) {
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function Accounts() {
  const viewContext  = useContext(ViewContext);
  const authContext  = useContext(AuthContext);
  const navigate     = useNavigate();

  const [search,       setSearch]       = useState('');
  const [sortKey,      setSortKey]      = useState('name');
  const [sortDir,      setSortDir]      = useState('asc');
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(50);
  const [openKebab,    setOpenKebab]    = useState(null);
  const [filters,      setFilters]      = useState(FILTER_DEFAULTS);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [refreshKey,   setRefreshKey]   = useState(0);

  const debouncedSearch = useDebounce(search, 800);

  const licenseRef    = useRef(null);
  const practiceRef   = useRef(null);
  const avgPromptsRef = useRef(null);
  const dateRef       = useRef(null);

  // Close chip dropdowns on outside click
  useEffect(() => {
    if (!openDropdown) return;
    function handler(e) {
      const refs = { license: licenseRef, practiceAreas: practiceRef, avgPrompts: avgPromptsRef, date: dateRef };
      const ref = refs[openDropdown];
      if (ref?.current && !ref.current.contains(e.target)) setOpenDropdown(null);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openDropdown]);

  // Close kebab on outside click
  useEffect(() => {
    if (openKebab === null) return;
    function handler() { setOpenKebab(null); }
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openKebab]);

  function toggleDropdown(name) {
    setOpenDropdown(prev => prev === name ? null : name);
  }

  function handleSetFilters(updater) {
    setFilters(updater);
    setPage(1);
  }

  function handleSetPageSize(size) {
    setPageSize(size);
    setPage(1);
  }

  function clearAllFilters() {
    handleSetFilters(FILTER_DEFAULTS);
    setOpenDropdown(null);
  }

  // Map UI sort keys to API field names (UI keys like _date_created_str don't exist on the backend)
  const SORT_PARAM_BY_KEY = {
    name:                       'name',
    email:                      'email',
    _date_created_str:          'date_created',
    active:                     'active',
    latest_prompts_per_month:   'latest_prompts_per_month',
    average_prompts_per_month:  'average_prompts_per_month',
  };

  // Build server-side query URL
  const accountsQuery = (() => {
    const params = new URLSearchParams();
    params.set('offset', (page - 1) * pageSize);
    params.set('limit', pageSize);
    params.set('sort', SORT_PARAM_BY_KEY[sortKey] ?? 'name');
    params.set('order', sortDir);
    if (debouncedSearch.trim())                        params.set('search', debouncedSearch.trim());
    if (filters.licenseTiers.length > 0)              params.set('license_tiers', filters.licenseTiers.join(','));
    if (filters.practiceAreasFilter.length > 0)       params.set('practice_areas', filters.practiceAreasFilter.join(','));
    if (filters.avgPromptsPreset)                     params.set('avg_prompts_preset', filters.avgPromptsPreset);
    if (filters.dateFrom)                             params.set('date_from', filters.dateFrom);
    if (filters.dateTo)                               params.set('date_to', filters.dateTo);
    if (refreshKey > 0)                               params.set('_t', refreshKey);
    return `/api/account?${params.toString()}`;
  })();

  const accounts = useAPI(accountsQuery);

  // Practice areas list comes from a dedicated endpoint (not derived from current page)
  const practiceAreasAPI = useAPI('/api/account/practice_areas');
  const allPracticeAreas = practiceAreasAPI.data || [];

  // Reset to page 1 when search changes (debounced — no direct handler to intercept)
  useEffect(() => { setPage(1); }, [debouncedSearch]);

  // Enrich current page rows with computed display fields
  const rawRows = useMemo(() => accounts.data?.results || [], [accounts.data]);
  const total   = accounts.data?.total || 0;

  const pageRows = useMemo(() => {
    return rawRows.map(acc => {
      const { tiers, practiceAreas } = parseLicenses(acc.licenses);
      return {
        ...acc,
        _date_created_str: convertToLocalTime(acc.date_created) || '',
        license:        tiers,
        practice_areas: practiceAreas,
      };
    });
  }, [rawRows]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  }

  function editAccount(acc) {
    viewContext.modal.show({
      title: STRINGS.editModalTitle,
      form: {
        id:   { type: 'hidden', value: acc.id },
        active: {
          label: STRINGS.editStatusLabel, type: 'select', default: acc.active,
          options: [{ value: 1, label: STRINGS.editStatusActive }, { value: 0, label: STRINGS.editStatusCancelled }],
          required: true,
        },
      },
      buttonText: STRINGS.editModalSave,
      url: buildWebappUrl(authContext.remote_server, '/api/account/plan'),
      method: 'PATCH',
      audit: { action: 'account.plan_updated', target_type: 'account', target_id: acc.id },
    }, () => {
      viewContext.notification.show(acc.name + ' was updated', 'success', true);
      setRefreshKey(k => k + 1);
    });
  }

  function deleteAccount(acc) {
    viewContext.modal.show({
      title: STRINGS.deleteModalTitle,
      form: {},
      buttonText: STRINGS.deleteModalBtn,
      text: `Are you sure you want to close ${acc.email}'s account?`,
      url: buildWebappUrl(authContext.remote_server, `/api/account/${acc.id}`),
      method: 'DELETE',
      destructive: true,
      audit: { action: 'account.deleted', target_type: 'account', target_id: acc.id },
    }, () => {
      viewContext.notification.show(`${acc.email} was deleted`, 'success', true);
      setRefreshKey(k => k + 1);
    });
  }

  const anyFilterActive = hasActiveFilter(filters);
  const loading = accounts.loading;

  return (
    <Animate>
      <h1 className={Style.pageTitle}>{STRINGS.pageTitle}</h1>
      <p className={Style.pageSubtitle}>{STRINGS.pageSubtitle}</p>

      {/* Search + Filter chips bar */}
      <div className={Style.topBar}>
        {/* Search */}
        <div className={Style.searchWrap}>
          <span className={Style.searchIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </span>
          <input
            type="text"
            placeholder={STRINGS.searchPlaceholder}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={Style.searchInput}
          />
        </div>

        {/* Inline filter chips */}
        <div className={Style.chipsBar}>
          <FilterChip
            label={licenseChipLabel(filters)}
            active={filters.licenseTiers.length > 0}
            onClick={() => toggleDropdown('license')}
            dropdownOpen={openDropdown === 'license'}
            dropdownRef={licenseRef}
          >
            <LicenseDropdown filters={filters} setFilters={handleSetFilters} />
          </FilterChip>

          <FilterChip
            label={practiceAreasChipLabel(filters)}
            active={filters.practiceAreasFilter.length > 0}
            onClick={() => toggleDropdown('practiceAreas')}
            dropdownOpen={openDropdown === 'practiceAreas'}
            dropdownRef={practiceRef}
          >
            <PracticeAreasDropdown
              filters={filters}
              setFilters={handleSetFilters}
              allPracticeAreas={allPracticeAreas}
            />
          </FilterChip>

          <FilterChip
            label={avgPromptsChipLabel(filters)}
            active={filters.avgPromptsPreset !== ''}
            onClick={() => toggleDropdown('avgPrompts')}
            dropdownOpen={openDropdown === 'avgPrompts'}
            dropdownRef={avgPromptsRef}
          >
            <AvgPromptsDropdown filters={filters} setFilters={handleSetFilters} />
          </FilterChip>

          <FilterChip
            label={dateChipLabel(filters)}
            active={filters.dateFrom !== '' || filters.dateTo !== ''}
            onClick={() => toggleDropdown('date')}
            dropdownOpen={openDropdown === 'date'}
            dropdownRef={dateRef}
          >
            <DateDropdown filters={filters} setFilters={handleSetFilters} />
          </FilterChip>

          {anyFilterActive && (
            <button onClick={clearAllFilters} className={Style.clearAllBtn}>
              {STRINGS.clearAll}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={COLS}
        rows={pageRows}
        loading={loading}
        emptyMessage={STRINGS.emptyMessage}
        sortKey={sortKey}
        sortDir={sortDir}
        onSort={toggleSort}
        page={page}
        pageSize={pageSize}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={handleSetPageSize}
        pageSizes={PAGE_SIZES}
        onRowClick={(acc, e) => {
          const url = `/accounts/${acc.id}`;
          if (e.ctrlKey || e.metaKey) openInNewTab(url);
          else navigate(url);
        }}
        onRowAuxClick={(acc) => openInNewTab(`/accounts/${acc.id}`)}
        renderCell={(key, _value, acc) => {
          if (key === 'name') return (
            <td key="name" className={Style.cellName}>
              <a
                href={`/accounts/${acc.id}`}
                onClick={e => { e.preventDefault(); e.stopPropagation(); if (e.ctrlKey || e.metaKey) openInNewTab(`/accounts/${acc.id}`); else navigate(`/accounts/${acc.id}`); }}
                onAuxClick={e => { if (e.button === 1) { e.preventDefault(); openInNewTab(`/accounts/${acc.id}`); } }}
                className={Style.cellNameLink}
              >
                {acc.name || '—'}
              </a>
            </td>
          );
          if (key === 'email') return (
            <td key="email" className={Style.cellEmail}>{acc.email || '—'}</td>
          );
          if (key === '_date_created_str') {
            const { date, time } = splitDateTime(acc._date_created_str);
            return (
              <td key="_date_created_str" className={Style.cellDate}>
                <span className={Style.cellDateMain}>{date}</span>
                {time && <><br /><span className={Style.cellDateTime}>{time}</span></>}
              </td>
            );
          }
          if (key === 'active') return (
            <td key="active" className={Style.cellStatus}><StatusBadge active={acc.active} /></td>
          );
          if (key === 'license') return (
            <td key="license" className={Style.cellLicense}><LicenseBadge tiers={acc.license} /></td>
          );
          if (key === 'practice_areas') return (
            <td key="practice_areas" className={Style.cellPractice}>
              <PracticeAreaTags areas={acc.practice_areas} />
            </td>
          );
          if (key === 'latest_prompts_per_month') return (
            <td key="latest_prompts_per_month" className={Style.cellLatestPrompts}>
              {acc.latest_prompts_per_month ?? 0}
            </td>
          );
          if (key === 'average_prompts_per_month') {
            const avgRounded = typeof acc.average_prompts_per_month === 'number'
              ? Math.round(acc.average_prompts_per_month * 100) / 100
              : 0;
            return (
              <td key="average_prompts_per_month" className={Style.cellAvgPrompts}>
                <MetricBadge value={avgRounded} />
              </td>
            );
          }
          if (key === '_actions') return (
            <td key="_actions" className={Style.cellActions} onClick={e => e.stopPropagation()}>
              <button
                onClick={e => { e.stopPropagation(); setOpenKebab(openKebab === acc.id ? null : acc.id); }}
                className={Style.kebabBtn}
              >
                ⋮
              </button>
              {openKebab === acc.id && (
                <div onClick={e => e.stopPropagation()} className={Style.kebabMenu}>
                  <button
                    onClick={() => { navigate(`/accounts/${acc.id}`); setOpenKebab(null); }}
                    className={`${Style.kebabItem} ${Style.kebabItemNormal}`}
                  >
                    {STRINGS.kebabView}
                  </button>
                  <button
                    onClick={() => { editAccount(acc); setOpenKebab(null); }}
                    className={`${Style.kebabItem} ${Style.kebabItemNormal}`}
                  >
                    {STRINGS.kebabEdit}
                  </button>
                  <button
                    onClick={() => { deleteAccount(acc); setOpenKebab(null); }}
                    className={`${Style.kebabItem} ${Style.kebabItemDanger}`}
                  >
                    {STRINGS.kebabDelete}
                  </button>
                </div>
              )}
            </td>
          );
          return null;
        }}
      />
    </Animate>
  );
}
